import type { CSSProperties, ReactNode } from 'react'
import { MARKETING_DIVIDER_BORDER } from '@/landing/landing-surfaces'
import { MARKETING_MOBILE_FOLD_INSET, MARKETING_STICKY_NAV_MD_INSET } from '@/landing/layouts/landing-layouts'
import { MARKETING_CONTENT_COLUMN } from '@/landing/marketing-layout-tokens'
import { cn } from '@/lib/utils'

type LandingCinematicHeroProps = {
  background?: ReactNode
  children: ReactNode
  ariaLabel?: string
  fullViewport?: boolean
  stackBelowMd?: boolean
  mobileFullScreen?: boolean
  mobileCopy?: ReactNode
  scrollBelow?: ReactNode
  underStickyNav?: boolean
  fullOpacityBackground?: boolean
  heroHeight?: 'default' | '90svh'
  contentColumnClass?: string
  contentColumnStyle?: CSSProperties
  className?: string
}

const mobileBandClass = 'aspect-[4/3] max-h-[50svh] min-h-[12rem]'
const cinematicHeroHeightClass = 'h-[90svh] min-h-[90svh] max-h-[90svh] aspect-auto'

function desktopBandClass(heroHeight: 'default' | '90svh'): string {
  return heroHeight === '90svh'
    ? cinematicHeroHeightClass
    : 'aspect-video max-h-[85svh] min-h-[min(18rem,50svh)]'
}

function stackedBandClass(heroHeight: 'default' | '90svh'): string {
  return heroHeight === '90svh'
    ? cn(mobileBandClass, 'md:h-[90svh] md:min-h-[90svh] md:max-h-[90svh] md:aspect-auto')
    : cn(mobileBandClass, 'md:aspect-video md:max-h-[85svh] md:min-h-[min(18rem,50svh)]')
}

export function LandingCinematicHero({
  background,
  children,
  ariaLabel,
  fullViewport = false,
  stackBelowMd = false,
  mobileFullScreen = false,
  mobileCopy,
  scrollBelow,
  underStickyNav = false,
  fullOpacityBackground = false,
  heroHeight = 'default',
  contentColumnClass = MARKETING_CONTENT_COLUMN,
  contentColumnStyle,
  className,
}: LandingCinematicHeroProps) {
  const insetX = fullViewport ? undefined : 'px-6 sm:px-8 md:px-10'
  const copyBelow = mobileCopy ?? children
  const mobileStack = stackBelowMd && !mobileFullScreen
  const desktopCopyClass = cn(
    insetX,
    'flex min-h-0 flex-1 flex-col justify-center py-6 sm:py-8 md:py-10',
    underStickyNav && MARKETING_STICKY_NAV_MD_INSET
  )
  const desktopOverlay = fullViewport ? (
    <div
      className={cn(contentColumnClass, 'flex h-full min-h-0 flex-col')}
      style={contentColumnStyle}
    >
      <div className={desktopCopyClass}>{children}</div>
    </div>
  ) : (
    <div className="flex h-full min-h-0 flex-col">
      <div className={desktopCopyClass}>{children}</div>
    </div>
  )
  const backgroundScrimClass = fullOpacityBackground
    ? null
    : 'pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-background/20 via-transparent to-background/80 max-md:from-transparent max-md:via-background/15 max-md:to-background/90 md:bg-gradient-to-r md:from-background/95 md:via-background/70 md:to-background/25 dark:max-md:to-background/95 md:dark:from-background/90 md:dark:via-background/55 md:dark:to-background/15'
  const stackedScrimClass = fullOpacityBackground
    ? null
    : 'pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-background/50 md:bg-gradient-to-r md:from-background/95 md:via-background/70 md:to-background/25 dark:to-background/60 md:dark:from-background/90 md:dark:via-background/55 md:dark:to-background/15'
  const desktopScrimClass = fullOpacityBackground
    ? null
    : 'pointer-events-none absolute inset-0 z-[1] bg-gradient-to-r from-background/95 via-background/70 to-background/25 dark:from-background/90 dark:via-background/55 dark:to-background/15'

  if (mobileFullScreen) {
    const mobileCinematicFold = Boolean(background)
    const sectionBandClass =
      heroHeight === '90svh'
        ? cinematicHeroHeightClass
        : cn(
            stackedBandClass(heroHeight),
            mobileCinematicFold
              ? 'max-md:min-h-[100svh] max-md:aspect-auto max-md:max-h-none'
              : 'max-md:aspect-auto max-md:max-h-none max-md:min-h-0'
          )

    return (
      <>
        <section
          className={cn('relative isolate w-full overflow-hidden', sectionBandClass, className)}
          {...(ariaLabel ? { role: 'img', 'aria-label': ariaLabel } : {})}
        >
          {background ? (
            <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
              {background}
            </div>
          ) : null}
          {backgroundScrimClass ? (
            <div className={backgroundScrimClass} aria-hidden />
          ) : null}
          <div
            className={cn(
              'relative z-10 flex flex-col',
              heroHeight === '90svh'
                ? 'h-full min-h-0'
                : mobileCinematicFold
                  ? 'min-h-[100svh] md:h-full md:min-h-0'
                  : 'md:h-full md:min-h-0'
            )}
          >
            <div
              className={cn(
                contentColumnClass,
                'flex flex-col justify-start gap-5 sm:gap-6 md:hidden',
                mobileCinematicFold ? cn(MARKETING_MOBILE_FOLD_INSET, 'flex-1') : 'py-6'
              )}
              style={contentColumnStyle}
            >
              {mobileCopy ? <div className="flex flex-col">{mobileCopy}</div> : null}
            </div>
            <div className="hidden h-full min-h-0 flex-col md:flex">{desktopOverlay}</div>
          </div>
        </section>
        {scrollBelow ? (
          <div
            className={cn(
              contentColumnClass,
              'flex flex-col gap-8 pb-8 pt-8 sm:pb-10 sm:pt-10 md:hidden'
            )}
            style={contentColumnStyle}
          >
            {scrollBelow}
          </div>
        ) : null}
      </>
    )
  }

  if (mobileStack) {
    return (
      <>
        <section
          className={cn(
            'relative isolate w-full overflow-hidden',
            stackedBandClass(heroHeight),
            className
          )}
          {...(ariaLabel ? { role: 'img', 'aria-label': ariaLabel } : {})}
        >
          {background ? (
            <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
              {background}
            </div>
          ) : null}
          {stackedScrimClass ? (
            <div className={stackedScrimClass} aria-hidden />
          ) : null}
          <div className="relative z-10 hidden h-full min-h-0 flex-col md:flex">{desktopOverlay}</div>
        </section>
        <div
          className={cn(
            contentColumnClass,
            'flex flex-col gap-5 pb-6 pt-6 sm:gap-6 sm:pt-8 md:hidden'
          )}
          style={contentColumnStyle}
        >
          {copyBelow}
        </div>
      </>
    )
  }

  return (
    <section
      className={cn(
        'relative isolate w-full overflow-hidden',
        desktopBandClass(heroHeight),
        !fullViewport && cn('rounded-xl border', MARKETING_DIVIDER_BORDER),
        className
      )}
      {...(ariaLabel ? { role: 'img', 'aria-label': ariaLabel } : {})}
    >
      {background ? (
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
          {background}
        </div>
      ) : null}

      {desktopScrimClass ? (
        <div className={desktopScrimClass} aria-hidden />
      ) : null}

      <div className="relative z-10 flex h-full min-h-0 flex-col">{desktopOverlay}</div>
    </section>
  )
}

export function LandingViewportBreakout({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('relative w-full overflow-x-hidden', className)}>
      <div className="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2">{children}</div>
    </div>
  )
}
